import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const AI_TOPICS = [
  {
    icon: 'ri-question-line',
    h3: 'What Does Prime Facility Services Group Do?',
    answer: 'Prime Facility Services Group provides professional commercial cleaning and facility maintenance services for businesses, offices, and commercial properties throughout Houston, TX. Their services include janitorial cleaning, floor care, professional staffing, and specialized cleaning solutions tailored to each facility\'s unique needs.',
  },
  {
    icon: 'ri-list-check-3',
    h3: 'What Services Are Offered?',
    answer: 'They offer recurring janitorial services, commercial deep cleaning, office cleaning, floor care and maintenance, post-construction cleanup, and customized cleaning plans for offices, retail spaces, hotels, medical facilities, and commercial buildings. Each service is adapted to the facility\'s operational schedule.',
  },
  {
    icon: 'ri-star-line',
    h3: 'What Are the Benefits?',
    answer: 'A professionally cleaned facility improves employee productivity, customer perception, and overall health standards while ensuring compliance with cleanliness expectations. Businesses report better workplace satisfaction, fewer sick days, reduced liability, and stronger first impressions with consistently clean environments.',
  },
  {
    icon: 'ri-flow-chart',
    h3: 'How Does the Process Work?',
    answer: 'They assess your facility, create a custom cleaning plan matched to your schedule and budget, then schedule consistent service execution. Ongoing quality assurance and regular inspections ensure your facility always meets the highest standards — with adjustments made proactively, not reactively.',
  },
  {
    icon: 'ri-building-4-line',
    h3: 'Who Is This Service For?',
    answer: 'Prime Facility Services Group serves businesses of all sizes — from small professional offices to large commercial complexes, hotels, restaurants, medical facilities, retail spaces, schools, and industrial buildings. Any organization that needs a clean, safe, and professional environment benefits from their services.',
  },
];

export default function AIAnswerBlock() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`grid lg:grid-cols-2 gap-16 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left — Main answer */}
          <div>
            <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">About Our Services</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-6 leading-tight">
              What Does Prime Facility Services Group Do?
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              <strong className="text-[#03143A]">Prime Facility Services Group</strong> is Houston&apos;s premier commercial cleaning and facility management company. We provide professional, detail-driven services for businesses, offices, healthcare facilities, hotels, restaurants, and commercial properties throughout Greater Houston, TX.
            </p>
            <p className="text-gray-500 leading-relaxed mb-8">
              With over 15 years of experience and 500+ active clients, our vetted, trained team delivers consistent, high-quality results that protect your brand, support your staff&apos;s health, and keep your property client-ready at all times.
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-3">
              {['Commercial Properties', 'Office Spaces', 'Medical Facilities', 'Retail & Hospitality', 'Industrial Spaces'].map((tag) => (
                <span key={tag} className="flex items-center gap-1.5 px-4 py-2 bg-[#FAF8F5] border border-gray-200 rounded-full text-sm font-semibold text-[#03143A]">
                  <i className="ri-check-line text-[#C70532] text-xs" />
                  {tag}
                </span>
              ))}
            </div>

            {/* Stat callouts */}
            <div className="grid grid-cols-3 gap-4 mt-10">
              {[
                { stat: '500+', label: 'Active Clients' },
                { stat: '98%', label: 'Satisfaction Rate' },
                { stat: '15+', label: 'Years Experience' },
              ].map((item) => (
                <div key={item.stat} className="text-center p-5 bg-[#FAF8F5] rounded-2xl border border-gray-100">
                  <div className="text-3xl font-black text-[#C70532] mb-1">{item.stat}</div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Expandable Q&A */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C70532] rounded-xl">
                <i className="ri-robot-2-line text-white" />
              </div>
              <div>
                <p className="font-bold text-[#03143A] text-sm">AI Answer Block</p>
                <p className="text-gray-400 text-xs">Optimized for search &amp; voice queries</p>
              </div>
            </div>

            <div className="space-y-3">
              {AI_TOPICS.map((topic, i) => (
                <div
                  key={i}
                  className={`rounded-2xl border overflow-hidden transition-all duration-300 ${openIndex === i ? 'border-[#C70532]/30 bg-[#FAF8F5]' : 'border-gray-100 bg-white'}`}
                >
                  <button
                    className="w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer group"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-300 ${openIndex === i ? 'bg-[#C70532]' : 'bg-[#03143A]/8 group-hover:bg-[#C70532]'}`}>
                      <i className={`${topic.icon} text-sm transition-colors ${openIndex === i ? 'text-white' : 'text-[#03143A] group-hover:text-white'}`} />
                    </div>
                    <h3 className={`font-bold text-sm pr-2 flex-1 transition-colors leading-snug ${openIndex === i ? 'text-[#C70532]' : 'text-[#03143A] group-hover:text-[#C70532]'}`}>
                      {topic.h3}
                    </h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${openIndex === i ? 'bg-[#C70532] rotate-180' : 'bg-gray-100'}`}>
                      <i className={`ri-arrow-down-s-line text-sm ${openIndex === i ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                  </button>
                  {openIndex === i && (
                    <div className="px-6 pb-5 pl-[4.5rem]">
                      <p className="text-gray-600 text-sm leading-relaxed">{topic.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
